import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  itIT,
} from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import * as XLSX from "xlsx";
import LinearProgress from "@mui/material/LinearProgress";
import Modal from "../../components/Modal";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import api from "../../api/api";
import { EXPORT_BUTTON_CLASS } from "../../utils/exportButtonStyle";
import { toast } from "react-toastify";
import { knowAcces } from "../../utils/utils";
import {
  HiOutlineCheck,
  HiOutlineTrash,
  HiPlus,
} from "react-icons/hi2";
import { MdEdit, MdTableChart } from "react-icons/md";
import { CustomFooterPersonalized } from "../../components/DataGrid/CustomFooterPersonalized";
import { formatItalianNumber } from "../../utils/formatItalianNumber";

const fmt = (v) =>
  v != null
    ? formatItalianNumber(v, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";

const importoToEditable = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value).replace(".", ",");
};

const parseImporto = (text) => {
  if (text === null || text === undefined) return "";
  return String(text).replace(/\./g, "").replace(/\s/g, "").replace(",", ".");
};

const formatImportoLive = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const s = String(value);
  const hasComma = s.includes(",");
  let [intRaw, decRaw = ""] = s.split(",");
  intRaw = intRaw.replace(/\D/g, "");
  decRaw = decRaw.replace(/\D/g, "").slice(0, 2);
  const grouped = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return hasComma ? `${grouped},${decRaw}` : grouped;
};

const normalizeCodiceListino = (codice) => {
  const raw = String(codice || "").trim();
  if (!raw) return "";
  const normalized = raw.replace(/^0+(?=\d)/, "");
  return normalized || "0";
};

const codiceExportValue = (codice) => {
  const normalized = normalizeCodiceListino(codice);
  if (!normalized) return "";
  return /^\d+$/.test(normalized) ? Number(normalized) : normalized;
};

const formatSezioneCrm = (value) =>
  String(value || "")
    .split("//")
    .map((part) => part.trim())
    .filter(Boolean)
    .join("/");

const getSezioneSortValue = (value) => {
  const sezioni = String(value || "")
    .split(/\/\/|\//)
    .map((part) => Number(String(part).trim()))
    .filter((num) => Number.isFinite(num));
  if (!sezioni.length) return 99;
  return Math.min(...sezioni);
};

const isFasciaRow = (row) => row?.min_fascia != null || row?.max_fascia != null;

const compareRateRows = (a, b) => {
  const sezioneDiff = getSezioneSortValue(a.sezione_crm) - getSezioneSortValue(b.sezione_crm);
  if (sezioneDiff !== 0) return sezioneDiff;

  const codeA = Number(a.codice_visualizzato);
  const codeB = Number(b.codice_visualizzato);
  if (Number.isFinite(codeA) && Number.isFinite(codeB) && codeA !== codeB) return codeA - codeB;

  return String(a.codice_visualizzato).localeCompare(String(b.codice_visualizzato), "it", {
    sensitivity: "base",
    numeric: true,
  });
};

const CR_CODE_IIPAY = new Set(["1243", "1005", "1236", "1238", "1245", "1447", "1446"]);
const isCrCodeIipay = (codice) => CR_CODE_IIPAY.has(normalizeCodiceListino(codice));

const getImportoLabel = (row) => {
  const metodo = Number(row?.metodo_fatturazione);
  if (metodo === 1 || metodo === 2) return "libero";
  if (metodo === 6) return "tabella";
  return fmt(row?.importo);
};

const getImportoExportValue = (row) => {
  const metodo = Number(row?.metodo_fatturazione);
  if (metodo === 1 || metodo === 2) return "libero";
  if (metodo === 6) return "tabella";
  return row?.importo != null ? Number(row.importo) : "";
};

export const RateCardIIpay = () => {
  const [open, setOpen] = useState(false);
  const [listini, setListini] = useState([]);
  const [listinoSel, setListinoSel] = useState(null);
  const [tariffe, setTariffe] = useState([]);
  const [extraRows, setExtraRows] = useState([]);
  const [view, setView] = useState("rate");
  const [fasciaSel, setFasciaSel] = useState("");
  const [rateSearch, setRateSearch] = useState("");
  const [extraSearch, setExtraSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [editListinoOpen, setEditListinoOpen] = useState(false);
  const [editListinoNome, setEditListinoNome] = useState("");
  const [editListinoId, setEditListinoId] = useState(null);

  const [editTariffaOpen, setEditTariffaOpen] = useState(false);
  const [editTariffa, setEditTariffa] = useState({});

  const isAdmin = knowAcces(["parc_admin"]);

  const getFasciaKey = (row) => `${row?.min_fascia ?? ""}:${row?.max_fascia ?? ""}`;
  const getFasciaLabel = (row) => {
    const min = row?.min_fascia;
    const max = row?.max_fascia;
    if (min == null && max == null) return "Senza fascia";
    if (min != null && max != null) return `${min}-${max}`;
    if (min != null) return `da ${min}`;
    return `fino a ${max}`;
  };

  const loadListini = useCallback(async () => {
    try {
      const res = await api.get("/tipologia_listino_iipay");
      setListini(res.data);
      if (!listinoSel && res.data.length > 0) setListinoSel(res.data[0].id);
    } catch {
      toast.error("Errore caricamento listini");
    }
  }, [listinoSel]);

  const loadTariffe = useCallback(async () => {
    if (!listinoSel) return;
    setLoading(true);
    try {
      const res = await api.get(`/tipologia_listino_iipay/${listinoSel}/tariffe`);
      setTariffe(res.data);
    } catch {
      toast.error("Errore caricamento tariffe");
    } finally {
      setLoading(false);
    }
  }, [listinoSel]);

  const loadExtra = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/tipologia_listino_iipay/extra/listini");
      setExtraRows(res.data);
    } catch {
      toast.error("Errore caricamento extra iiPay");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadListini();
  }, [open, loadListini]);

  useEffect(() => {
    if (open) loadTariffe();
  }, [listinoSel, open, loadTariffe]);

  useEffect(() => {
    if (open && view === "extra") loadExtra();
  }, [open, view, loadExtra]);

  const fasce = useMemo(() => {
    const map = new Map();
    tariffe.filter(isFasciaRow).forEach((row) => {
      const key = getFasciaKey(row);
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: getFasciaLabel(row),
          min_fascia: row.min_fascia,
          max_fascia: row.max_fascia,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const aFlat = a.min_fascia == null && a.max_fascia == null;
      const bFlat = b.min_fascia == null && b.max_fascia == null;
      if (aFlat !== bFlat) return aFlat ? 1 : -1;
      return (a.min_fascia ?? -1) - (b.min_fascia ?? -1);
    });
  }, [tariffe]);

  useEffect(() => {
    if (!fasce.length) {
      setFasciaSel("");
      return;
    }
    if (!fasce.some((fascia) => fascia.key === fasciaSel)) {
      setFasciaSel(fasce[0].key);
    }
  }, [fasce, fasciaSel]);

  const rateRows = useMemo(() => {
    const q = rateSearch.trim().toLowerCase();
    return tariffe
      .filter((row) => {
        if (!isFasciaRow(row)) return true;
        if (!fasciaSel) return false;
        return getFasciaKey(row) === fasciaSel;
      })
      .map((row) => ({
        ...row,
        codice_visualizzato: normalizeCodiceListino(row.nuovo_codice_ago || row.prestazione),
        cr_code: isCrCodeIipay(row.nuovo_codice_ago || row.prestazione) ? "Sì" : "",
        importo_visualizzato: getImportoLabel(row),
        tipo_importo: isFasciaRow(row) ? "A fascia" : "Fisso",
      }))
      .filter((row) => {
        if (!q) return true;
        return (
          String(row.codice_visualizzato || "").toLowerCase().includes(q) ||
          String(row.descrizione_prestazione || "").toLowerCase().includes(q)
        );
      })
      .sort(compareRateRows);
  }, [tariffe, fasciaSel, rateSearch]);

  const extraGroups = useMemo(() => {
    const groups = [];
    for (let i = 0; i < extraRows.length; i += 1) {
      const row = extraRows[i];
      const group = {
        fees: row.fees || "",
        gruppo: row.gruppo || "",
        rows: [row],
      };

      if (group.fees || group.gruppo) {
        let j = i + 1;
        while (
          j < extraRows.length &&
          !String(extraRows[j].fees || "").trim() &&
          !String(extraRows[j].gruppo || "").trim()
        ) {
          group.rows.push(extraRows[j]);
          j += 1;
        }
        i = j - 1;
      }

      groups.push(group);
    }

    const q = extraSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((group) => {
      const groupText = `${group.fees} ${group.gruppo}`.toLowerCase();
      return groupText.includes(q) || group.rows.some((row) =>
        [
          row.numero,
          row.action,
          row.document_required,
          row.miller_will_produce,
        ].some((value) => String(value || "").toLowerCase().includes(q))
      );
    });
  }, [extraRows, extraSearch]);

  const handleExportRateCard = () => {
    if (!tariffe.length) {
      toast.warning("Nessuna tariffa da esportare");
      return;
    }

    const listino = listini.find((item) => item.id === listinoSel);
    const q = rateSearch.trim().toLowerCase();
    const fasciaColumns = fasce.map((fascia) => ({
      ...fascia,
      header: `Fascia ${fascia.label}`,
    }));
    const groupedRows = new Map();

    tariffe.forEach((row) => {
      const codice = normalizeCodiceListino(row.nuovo_codice_ago || row.prestazione);
      const searchText = `${codice} ${row.descrizione_prestazione || ""}`.toLowerCase();
      if (q && !searchText.includes(q)) return;

      const key = `${row.prestazione || ""}:${codice}`;
      if (!groupedRows.has(key)) {
        groupedRows.set(key, {
          base: {
            ...row,
            codice_visualizzato: codice,
          },
          fixed: null,
          byFascia: new Map(),
        });
      }

      const group = groupedRows.get(key);
      if (isFasciaRow(row)) {
        group.byFascia.set(getFasciaKey(row), row);
      } else {
        group.fixed = row;
      }
    });

    const exportRows = Array.from(groupedRows.values())
      .sort((a, b) => compareRateRows(a.base, b.base))
      .map((group) => {
        const hasFascia = group.byFascia.size > 0;
        const fixedRow = group.fixed || group.base;
        const out = {
          Codice: codiceExportValue(group.base.codice_visualizzato),
          Prestazione: group.base.descrizione_prestazione || "",
          "CR Code": isCrCodeIipay(group.base.codice_visualizzato) ? "Sì" : "",
          "Sezioni CRM": formatSezioneCrm(group.base.sezione_crm),
          Tipo: hasFascia ? "A fascia" : "Fisso",
          "Importo fisso": group.fixed ? getImportoExportValue(group.fixed) : "",
          "Qta obbligatoria": Number(fixedRow.quantita_obbligatoria) === 1 ? "Sì" : "",
          "Descrizione obbligatoria": String(fixedRow.richiesta || "").trim() ? "Sì" : "",
          Note: fixedRow.note || "",
        };

        fasciaColumns.forEach((fascia) => {
          out[fascia.header] = group.byFascia.has(fascia.key)
            ? getImportoExportValue(group.byFascia.get(fascia.key))
            : "";
        });

        return out;
      });

    if (!exportRows.length) {
      toast.warning("Nessuna riga da esportare con i filtri correnti");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const moneyColumns = [5, ...fasciaColumns.map((_, index) => 9 + index)];
    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let rowIndex = 1; rowIndex <= range.e.r; rowIndex += 1) {
        moneyColumns.forEach((colIndex) => {
          const cell = ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })];
          if (cell && cell.t === "n") cell.z = "#,##0.00";
        });
      }
    }
    ws["!cols"] = [
      { wch: 10 },
      { wch: 42 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 16 },
      { wch: 22 },
      { wch: 34 },
      ...fasciaColumns.map(() => ({ wch: 14 })),
    ];
    if (ws["!ref"]) {
      ws["!autofilter"] = { ref: ws["!ref"] };
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rate card");
    const safeName = String(listino?.name || "iipay")
      .replace(/[\\/:*?"<>|]+/g, "_")
      .replace(/\s+/g, "_")
      .slice(0, 60);
    XLSX.writeFile(wb, `rate_card_iipay_${safeName}.xlsx`);
  };

  // ─── Listino CRUD ────────────────────────────────────────────────────────────

  const handleSaveListino = async () => {
    if (!editListinoNome.trim()) return;
    try {
      if (editListinoId) {
        await api.put(`/tipologia_listino_iipay/${editListinoId}`, { descrizione: editListinoNome });
      } else {
        const res = await api.post("/tipologia_listino_iipay", { descrizione: editListinoNome });
        setListinoSel(res.data.id);
      }
      await loadListini();
      setEditListinoOpen(false);
    } catch {
      toast.error("Errore salvataggio listino");
    }
  };

  const handleDeleteListino = async (id) => {
    if (!window.confirm("Eliminare questo listino e tutte le sue tariffe?")) return;
    try {
      await api.delete(`/tipologia_listino_iipay/${id}`);
      setListinoSel(null);
      await loadListini();
    } catch {
      toast.error("Errore eliminazione listino");
    }
  };

  // ─── Tariffa CRUD ────────────────────────────────────────────────────────────

  const handleSaveTariffa = async () => {
    if (!editTariffa.prestazione || !editTariffa.importo) {
      toast.warning("Prestazione e importo sono obbligatori");
      return;
    }
    try {
      const payload = {
        tipologia_listino_iipay: listinoSel,
        prestazione: parseInt(editTariffa.prestazione),
        importo: parseFloat(editTariffa.importo),
        min_fascia: editTariffa.min_fascia ? parseInt(editTariffa.min_fascia) : null,
        max_fascia: editTariffa.max_fascia ? parseInt(editTariffa.max_fascia) : null,
      };
      if (editTariffa.id) {
        await api.put(`/tipologia_listino_iipay/tariffe/${editTariffa.id}`, payload);
      } else {
        await api.post("/tipologia_listino_iipay/tariffe", payload);
      }
      await loadTariffe();
      setEditTariffaOpen(false);
    } catch {
      toast.error("Errore salvataggio tariffa");
    }
  };

  const handleDeleteTariffa = async (id) => {
    try {
      await api.delete(`/tipologia_listino_iipay/tariffe/${id}`);
      await loadTariffe();
    } catch {
      toast.error("Errore eliminazione tariffa");
    }
  };

  // ─── Columns ─────────────────────────────────────────────────────────────────

  const columns = [
    {
      field: "prestazione",
      headerName: "Cod.",
      width: 65,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => p.row.codice_visualizzato,
    },
    {
      field: "descrizione_prestazione",
      headerName: "Prestazione",
      flex: 2,
      minWidth: 260,
    },
    {
      field: "cr_code",
      headerName: "CR Code",
      width: 92,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "sezione_crm",
      headerName: "Sez.",
      width: 76,
      headerAlign: "center",
      align: "center",
      valueFormatter: (p) => formatSezioneCrm(p.value),
    },
    {
      field: "importo_visualizzato",
      headerName: "Importo €",
      width: 130,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "tipo_importo",
      headerName: "Tipo",
      width: 92,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => (
        <span
          className={`rounded px-2 py-0.5 text-xs font-semibold ${
            p.value === "A fascia"
              ? "bg-blue-50 text-blue-700 border border-blue-200"
              : "bg-gray-50 text-gray-600 border border-gray-200"
          }`}
        >
          {p.value}
        </span>
      ),
    },
    ...(isAdmin
      ? [
          {
            field: "_azioni",
            headerName: "",
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <div className="flex gap-1">
                <Button
                  className="edit-button"
                  type="button"
                  icon={<MdEdit className="button-icon" />}
                  onClick={() => {
                    setEditTariffa({ ...params.row });
                    setEditTariffaOpen(true);
                  }}
                />
                <Button
                  className="delete-button"
                  type="button"
                  icon={<HiOutlineTrash className="button-icon" />}
                  onClick={() => handleDeleteTariffa(params.row.id)}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Button
        className="edit-button"
        text="Rate card"
        type="button"
        icon={<MdTableChart className="button-icon" />}
        onClick={() => setOpen(true)}
      />

      <Modal
        isOpen={open}
        closeButton={() => setOpen(false)}
        headerText="Rate card iiPay"
        size="large"
      >
        <div className="flex flex-col gap-3 h-[70vh]">
          <div className="flex items-center gap-2">
            {[
              { id: "rate", label: "Rate card" },
              { id: "extra", label: "Extra" },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setView(item.id)}
                className={`px-3 py-1 rounded border text-sm font-medium ${
                  view === item.id
                    ? "bg-green-100 text-green-700 border-green-300"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Selezione listino + gestione ADMIN */}
          {view === "rate" && <div className="flex items-center gap-2 flex-wrap">
            <div className="w-48">
              <Select
                label="Listino"
                placeholder="Seleziona..."
                value={listinoSel || ""}
                onChange={(e) => setListinoSel(parseInt(e.target.value))}
                options={listini.map((l) => ({ id: l.id, name: l.name }))}
              />
            </div>

            {isAdmin && (
              <div className="flex gap-2 mt-4">
                <Button
                  className="principal-button"
                  text="Nuovo listino"
                  type="button"
                  icon={<HiPlus className="button-icon" />}
                  onClick={() => {
                    setEditListinoId(null);
                    setEditListinoNome("");
                    setEditListinoOpen(true);
                  }}
                />
                {listinoSel && (
                  <>
                    <Button
                      className="edit-button"
                      type="button"
                      icon={<MdEdit className="button-icon" />}
                      onClick={() => {
                        const l = listini.find((x) => x.id === listinoSel);
                        setEditListinoId(listinoSel);
                        setEditListinoNome(l?.name || "");
                        setEditListinoOpen(true);
                      }}
                    />
                    <Button
                      className="delete-button"
                      type="button"
                      icon={<HiOutlineTrash className="button-icon" />}
                      onClick={() => handleDeleteListino(listinoSel)}
                    />
                  </>
                )}
              </div>
            )}
          </div>}
          {view === "rate" && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-gray-600 uppercase">Fascia cedolini</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {fasce.map((fascia) => (
                      <button
                        key={fascia.key}
                        type="button"
                        onClick={() => setFasciaSel(fascia.key)}
                        className={`px-3 py-1 rounded border text-sm font-medium ${
                          fasciaSel === fascia.key
                            ? "bg-green-100 text-green-700 border-green-300"
                            : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {fascia.label}
                      </button>
                    ))}
                  </div>
                </div>
                {isAdmin && (
                  <Button
                    className="principal-button"
                    text="Aggiungi tariffa"
                    type="button"
                    icon={<HiPlus className="button-icon" />}
                    onClick={() => {
                      const fascia = fasce.find((item) => item.key === fasciaSel);
                      setEditTariffa({
                        tipologia_listino_iipay: listinoSel,
                        min_fascia: fascia?.min_fascia ?? null,
                        max_fascia: fascia?.max_fascia ?? null,
                      });
                      setEditTariffaOpen(true);
                    }}
                  />
                )}
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Input
                  type="text"
                  placeholder="Cerca codice o voce..."
                  value={rateSearch}
                  onChange={(e) => setRateSearch(e.target.value)}
                />
                <div className="text-xs text-gray-500">
                  {rateRows.length} voci visibili · badge "A fascia" per importi soggetti a fascia cedolini
                </div>
                <Button
                  className={EXPORT_BUTTON_CLASS}
                  text="Excel"
                  type="button"
                  icon={<MdTableChart className="button-icon" />}
                  onClick={handleExportRateCard}
                />
              </div>
            </div>
          )}

          {/* Griglia tariffe */}
          <div className="flex-1 min-h-0">
            {view === "rate" ? (
              <DataGridPro
                density="compact"
                slots={{ footer: CustomFooterPersonalized, loadingOverlay: LinearProgress }}
                loading={loading}
                localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
                columns={columns}
                rows={rateRows}
                getRowId={(r) => r.id}
              />
            ) : (
              <div className="flex h-full min-h-0 flex-col gap-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="w-full md:w-96">
                    <Input
                      type="text"
                      placeholder="Cerca extra..."
                      value={extraSearch}
                      onChange={(e) => setExtraSearch(e.target.value)}
                      showClear
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    {extraGroups.reduce((acc, group) => acc + group.rows.length, 0)} righe
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-auto border rounded-md bg-white">
                  {loading ? (
                    <LinearProgress />
                  ) : (
                    <table className="min-w-full border-collapse text-sm">
                      <thead className="sticky top-0 z-10 bg-sky-500 text-white">
                        <tr>
                          <th className="w-14 border-b border-sky-600 px-3 py-2 text-left font-semibold">#</th>
                          <th className="min-w-[260px] border-b border-sky-600 px-3 py-2 text-left font-semibold">Action</th>
                          <th className="min-w-[260px] border-b border-sky-600 px-3 py-2 text-left font-semibold">Document required</th>
                          <th className="min-w-[220px] border-b border-sky-600 px-3 py-2 text-left font-semibold">Miller will produce</th>
                          <th className="min-w-[220px] border-b border-sky-600 px-3 py-2 text-left font-semibold">Fees</th>
                          <th className="min-w-[220px] border-b border-sky-600 px-3 py-2 text-left font-semibold">Gruppo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {extraGroups.map((group) =>
                          group.rows.map((row, index) => (
                            <tr key={row.id} className="border-b border-gray-200 align-top">
                              <td className="px-3 py-2 font-medium text-gray-700">{row.numero}</td>
                              <td className="whitespace-pre-wrap px-3 py-2 text-gray-800">{row.action || "-"}</td>
                              <td className="whitespace-pre-wrap px-3 py-2 text-gray-700">{row.document_required || "-"}</td>
                              <td className="whitespace-pre-wrap px-3 py-2 text-gray-700">{row.miller_will_produce || "-"}</td>
                              {index === 0 && (
                                <>
                                  <td
                                    rowSpan={group.rows.length}
                                    className="whitespace-pre-wrap border-l border-gray-200 bg-blue-50 px-3 py-2 font-semibold text-blue-900"
                                  >
                                    {group.fees || "-"}
                                  </td>
                                  <td
                                    rowSpan={group.rows.length}
                                    className="whitespace-pre-wrap border-l border-gray-200 bg-gray-50 px-3 py-2 font-medium text-gray-800"
                                  >
                                    {group.gruppo || "-"}
                                  </td>
                                </>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Modale edit listino */}
      <Modal
        isOpen={editListinoOpen}
        closeButton={() => setEditListinoOpen(false)}
        headerText={editListinoId ? "Rinomina listino" : "Nuovo listino"}
        size="small"
        leftButtons={
          <Button
            className="principal-button"
            text="Salva"
            type="button"
            icon={<HiOutlineCheck className="button-icon" />}
            onClick={handleSaveListino}
          />
        }
      >
        <div className="p-2">
          <Input
            label="Nome listino"
            type="text"
            value={editListinoNome}
            onChange={(e) => setEditListinoNome(e.target.value)}
          />
        </div>
      </Modal>

      {/* Modale edit tariffa */}
      <Modal
        isOpen={editTariffaOpen}
        closeButton={() => setEditTariffaOpen(false)}
        headerText={editTariffa.id ? "Modifica tariffa" : "Nuova tariffa"}
        size="medium"
        leftButtons={
          <Button
            className="principal-button"
            text="Salva"
            type="button"
            icon={<HiOutlineCheck className="button-icon" />}
            onClick={handleSaveTariffa}
          />
        }
      >
        <div className="grid grid-cols-2 gap-3 p-2">
          <div className="col-span-2">
            <Input
              label="Codice prestazione"
              type="number"
              value={editTariffa.prestazione || ""}
              onChange={(e) => setEditTariffa({ ...editTariffa, prestazione: e.target.value })}
              disabled={!!editTariffa.id}
            />
          </div>
          <div>
            <Input
              label="Da cedolini (fascia min)"
              type="number"
              value={editTariffa.min_fascia ?? ""}
              placeholder="—"
              onChange={(e) => setEditTariffa({ ...editTariffa, min_fascia: e.target.value })}
            />
          </div>
          <div>
            <Input
              label="A cedolini (fascia max)"
              type="number"
              value={editTariffa.max_fascia ?? ""}
              placeholder="—"
              onChange={(e) => setEditTariffa({ ...editTariffa, max_fascia: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <Input
              label="Importo € *"
              type="text"
              value={formatImportoLive(importoToEditable(editTariffa.importo))}
              onChange={(e) => setEditTariffa({ ...editTariffa, importo: parseImporto(e.target.value) })}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};
